/**
 * SoftphoneBar — Compact call control bar
 * Shows at the top of Communications when a call is active or incoming
 */

import React from 'react';
import {
  Phone,
  PhoneOff,
  PhoneIncoming,
  Mic,
  MicOff,
  Wifi,
  WifiOff,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { SoftphoneStatus, ActiveCall } from '@/hooks/useTwilioDevice';
type Call = any;

interface SoftphoneBarProps {
  deviceStatus: SoftphoneStatus;
  activeCall: ActiveCall | null;
  incomingCall: Call | null;
  formattedDuration: string;
  onInitialize: () => void;
  onHangUp: () => void;
  onToggleMute: () => void;
  onAccept: () => void;
  onReject: () => void;
}

const statusLabels: Record<SoftphoneStatus, string> = {
  offline: 'Hors ligne',
  initializing: 'Connexion…',
  ready: 'En ligne',
  busy: 'En appel',
  error: 'Erreur',
};

const statusColors: Record<SoftphoneStatus, string> = {
  offline: 'bg-white/10 text-white/40',
  initializing: 'bg-amber-500/20 text-amber-400',
  ready: 'bg-emerald-500/20 text-emerald-400',
  busy: 'bg-red-500/20 text-red-400',
  error: 'bg-red-500/20 text-red-400',
};

export function SoftphoneBar({
  deviceStatus,
  activeCall,
  incomingCall,
  formattedDuration,
  onInitialize,
  onHangUp,
  onToggleMute,
  onAccept,
  onReject,
}: SoftphoneBarProps) {
  // ─── Incoming call banner ─────────────────────────────────────
  if (incomingCall) {
    return (
      <div className="h-12 px-4 border-b border-emerald-500/20 bg-emerald-500/10 flex items-center justify-between animate-pulse">
        <div className="flex items-center gap-2">
          <PhoneIncoming className="w-4 h-4 text-emerald-400" />
          <span className="text-sm text-emerald-300 font-medium">
            Appel entrant: {incomingCall.parameters?.From || 'Inconnu'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="h-7 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={onAccept}
          >
            <Phone className="w-3.5 h-3.5" /> Accepter
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 border-red-500/30 text-red-400 hover:bg-red-500/10"
            onClick={onReject}
          >
            <PhoneOff className="w-3.5 h-3.5" /> Refuser
          </Button>
        </div>
      </div>
    );
  }

  // ─── Active call bar ──────────────────────────────────────────
  if (activeCall) {
    return (
      <div className="h-12 px-4 border-b border-red-500/20 bg-red-500/5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-sm text-white font-medium">
            {activeCall.direction === 'outbound' ? '→' : '←'} {activeCall.to || activeCall.from}
          </span>
          <Badge variant="secondary" className="text-[10px] font-mono bg-white/10 text-white/70">
            {activeCall.status === 'connecting' || activeCall.status === 'ringing'
              ? (activeCall.status === 'ringing' ? 'Sonnerie…' : 'Connexion…')
              : formattedDuration}
          </Badge>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            size="icon"
            variant="ghost"
            className={`h-8 w-8 ${activeCall.isMuted ? 'text-red-400 bg-red-500/10' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
            onClick={onToggleMute}
          >
            {activeCall.isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
          <Button
            size="sm"
            className="h-8 gap-1.5 bg-red-600 hover:bg-red-700 text-white"
            onClick={onHangUp}
          >
            <PhoneOff className="w-3.5 h-3.5" /> Raccrocher
          </Button>
        </div>
      </div>
    );
  }

  // ─── Status indicator (compact) ───────────────────────────────
  return (
    <div className="h-8 px-4 border-b border-white/5 bg-[hsl(220,20%,9%)] flex items-center justify-between">
      <div className="flex items-center gap-2">
        {deviceStatus === 'initializing' ? (
          <Loader2 className="w-3 h-3 text-amber-400 animate-spin" />
        ) : deviceStatus === 'ready' ? (
          <Wifi className="w-3 h-3 text-emerald-400" />
        ) : (
          <WifiOff className="w-3 h-3 text-white/30" />
        )}
        <span className={`text-[10px] font-medium ${deviceStatus === 'ready' ? 'text-emerald-400/70' : 'text-white/30'}`}>
          {statusLabels[deviceStatus]}
        </span>
      </div>
      {deviceStatus === 'offline' && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 text-[10px] text-white/40 hover:text-white gap-1"
          onClick={onInitialize}
        >
          <Phone className="w-3 h-3" /> Connecter le téléphone
        </Button>
      )}
    </div>
  );
}
